import {
  CURRICULUM_COURSE_SPLIT_OLD_TO_NEW_LESSON_KEY,
  canonicalCurriculumLessonKey,
  curriculumLessonKeyAliases,
} from "../../../../../packages/shared-ui/src/utils/curriculum-course-split";

export interface CourseMistakeOwnership {
  course: string;
  lesson: string;
}

export const JAVASCRIPT_MISTAKE_COURSE_SLUGS = Object.freeze([
  "javascript-basics",
  "javascript-core",
  "javascript-dom",
  "javascript-practical",
  "javascript-async-api",
] as const);

export const JAVASCRIPT_MISTAKE_COURSE_TITLES: Readonly<
  Record<(typeof JAVASCRIPT_MISTAKE_COURSE_SLUGS)[number], string>
> = Object.freeze({
  "javascript-basics": "JavaScript入門：コードを動かす",
  "javascript-core": "JavaScript基礎文法：処理を組み立てる",
  "javascript-dom": "JavaScriptでWebページを動かす",
  "javascript-practical": "モダンJavaScript：データ処理とコード設計",
  "javascript-async-api": "非同期JavaScript：API通信とモジュール",
});

export const JAVASCRIPT_MISTAKE_STATIC_COURSE_TITLES: Readonly<
  Record<string, string>
> = Object.freeze({
  "javascript-basics": "JavaScript入門：プロフィールサイトを動かす",
  "javascript-practical": "実践JavaScript：外のデータでアプリを動かす",
});

export function publicCourseMistakeCourseTitle(
  courseSlug: string,
  fallback: string,
): string {
  return (
    (JAVASCRIPT_MISTAKE_COURSE_TITLES as Readonly<Record<string, string>>)[
      courseSlug
    ] ?? fallback
  );
}

export function staticCourseMistakeCourseTitle(
  courseSlug: string,
  fallback: string,
): string {
  return JAVASCRIPT_MISTAKE_STATIC_COURSE_TITLES[courseSlug] ?? fallback;
}

function splitCourse(key: string): string {
  const slash = key.indexOf("/");
  return slash < 0 ? key : key.slice(0, slash);
}

/**
 * Static mistake rows retain the pre-split key as their one-copy identity.
 * A post-migration DB export is folded back to that identity before writing.
 */
export function staticCourseMistakeLessonKey(
  row: CourseMistakeOwnership,
): string {
  return canonicalCurriculumLessonKey(`${row.course}/${row.lesson}`);
}

export function staticCourseMistakeKey(key: string): string {
  return canonicalCurriculumLessonKey(key);
}

export function staticCourseMistakeCourseSlug(
  row: CourseMistakeOwnership,
): string {
  return splitCourse(staticCourseMistakeLessonKey(row));
}

/** Public ownership is the target five-course topology in either DB phase. */
export function publicCourseMistakeLessonKey(
  row: CourseMistakeOwnership,
): string {
  const staticKey = staticCourseMistakeLessonKey(row);
  return (
    CURRICULUM_COURSE_SPLIT_OLD_TO_NEW_LESSON_KEY[staticKey] ?? staticKey
  );
}

export function publicCourseMistakeCourseSlug(
  row: CourseMistakeOwnership,
): string {
  return splitCourse(publicCourseMistakeLessonKey(row));
}

/** Normalize a DB-derived row without changing any mistake content. */
export function toStaticCourseMistakeRow<T extends CourseMistakeOwnership>(
  row: T,
): T {
  const course = staticCourseMistakeCourseSlug(row);
  return course === row.course ? row : { ...row, course };
}

/**
 * Old and new copies of the same row must never be emitted together. Keeping
 * both would inflate totals and the error-name index while looking plausible.
 */
export function assertUniquePublicCourseMistakes<
  T extends CourseMistakeOwnership & { heading: string; detail: string },
>(rows: readonly T[]): void {
  const seen = new Set<string>();
  for (const row of rows) {
    const identity = [
      publicCourseMistakeLessonKey(row),
      row.heading,
      row.detail,
    ].join("\u0000");
    if (seen.has(identity)) {
      throw new Error(
        `duplicate projected course mistake: ${publicCourseMistakeLessonKey(row)} / ${row.heading}`,
      );
    }
    seen.add(identity);
  }
}

export interface CourseMistakeContent extends CourseMistakeOwnership {
  lessonTitle: string;
  heading: string;
  detail: string;
}

export interface CourseMistakeCatalogCourse {
  courseSlug: string;
  label?: string;
  title?: string;
  lessons: ReadonlyArray<{ key: string }>;
}

export interface MaterializedMistakeErrorCourse {
  course: string;
  courseTitle: string;
  count: number;
  anchor: string;
}

export interface MaterializedMistakeErrorEntry {
  name: string;
  total: number;
  courses: MaterializedMistakeErrorCourse[];
}

export interface MaterializedCourseMistakes<T extends CourseMistakeContent> {
  rows: T[];
  courseTitles: Map<string, string>;
  courseCounts: Map<string, number>;
  courseSlugs: string[];
  previews: Map<string, string[]>;
  errorIndex: MaterializedMistakeErrorEntry[];
  total: number;
  unresolvedLessonKeys: string[];
}

function targetOrSourceJavascriptTitle(
  courseSlug: string,
  activeLessonKeys: ReadonlySet<string>,
): string | undefined {
  const targetTitle = (
    JAVASCRIPT_MISTAKE_COURSE_TITLES as Readonly<Record<string, string>>
  )[courseSlug];
  if (!targetTitle) return undefined;
  const hasLegacyMovedLesson =
    courseSlug === "javascript-basics" || courseSlug === "javascript-practical"
      ? [...activeLessonKeys].some(
          (key) =>
            key.startsWith(`${courseSlug}/`) &&
            CURRICULUM_COURSE_SPLIT_OLD_TO_NEW_LESSON_KEY[key] !== undefined,
        )
      : false;
  return hasLegacyMovedLesson
    ? JAVASCRIPT_MISTAKE_STATIC_COURSE_TITLES[courseSlug]
    : targetTitle;
}

function materializedCourseTitle(
  courseSlug: string,
  activeLessonKeys: ReadonlySet<string>,
  catalogTitle: string | undefined,
  fallbackTitles: ReadonlyMap<string, string>,
): string {
  return (
    targetOrSourceJavascriptTitle(courseSlug, activeLessonKeys) ??
    catalogTitle ??
    fallbackTitles.get(courseSlug) ??
    courseSlug
  );
}

function buildMaterializedErrorIndex<T extends CourseMistakeContent>(
  rows: readonly T[],
  courseTitles: ReadonlyMap<string, string>,
): MaterializedMistakeErrorEntry[] {
  const errorPattern = /\b([A-Z][A-Za-z]*(?:Error|Exception|Warning))\b/g;
  const anchors = new Map<T, string>();
  let lesson = "";
  let index = 0;
  for (const row of rows) {
    if (row.lesson !== lesson) {
      lesson = row.lesson;
      index = 0;
    }
    anchors.set(row, `mistake-${row.lesson}-${index}`);
    index++;
  }

  const byError = new Map<
    string,
    Map<string, MaterializedMistakeErrorCourse>
  >();
  for (const row of rows) {
    const names = new Set(
      [...`${row.heading} ${row.detail}`.matchAll(errorPattern)].map(
        (match) => match[1],
      ),
    );
    for (const name of names) {
      const byCourse = byError.get(name) ?? new Map();
      byError.set(name, byCourse);
      const existing = byCourse.get(row.course);
      if (existing) {
        existing.count++;
      } else {
        byCourse.set(row.course, {
          course: row.course,
          courseTitle: courseTitles.get(row.course) ?? row.course,
          count: 1,
          anchor: anchors.get(row) ?? "",
        });
      }
    }
  }

  return [...byError.entries()]
    .map(([name, byCourse]) => {
      const courses = [...byCourse.values()].sort(
        (left, right) => right.count - left.count,
      );
      return {
        name,
        total: courses.reduce((sum, course) => sum + course.count, 0),
        courses,
      };
    })
    .sort(
      (left, right) =>
        right.total - left.total || left.name.localeCompare(right.name),
    );
}

/**
 * Select exactly one public owner from the lesson keys present in the active
 * catalog. Phase A selects old keys, Phase B selects new keys. Both at once is
 * a migration-contract violation and fails closed instead of double-counting.
 */
export function materializeCourseMistakes<T extends CourseMistakeContent>(
  sourceRows: readonly T[],
  catalog: readonly CourseMistakeCatalogCourse[],
  fallbackTitles: ReadonlyMap<string, string> = new Map(),
): MaterializedCourseMistakes<T> {
  const activeLessonKeys = new Set<string>();
  const catalogTitles = new Map<string, string>();
  const courseOrder: string[] = [];
  for (const course of catalog) {
    if (!catalogTitles.has(course.courseSlug))
      courseOrder.push(course.courseSlug);
    catalogTitles.set(
      course.courseSlug,
      course.title ?? course.label ?? course.courseSlug,
    );
    for (const lesson of course.lessons) {
      if (!lesson.key.startsWith(`${course.courseSlug}/`)) {
        throw new Error(
          `mistake catalog key/course mismatch: ${course.courseSlug} / ${lesson.key}`,
        );
      }
      if (activeLessonKeys.has(lesson.key)) {
        throw new Error(`duplicate mistake catalog lesson key: ${lesson.key}`);
      }
      activeLessonKeys.add(lesson.key);
    }
  }

  const canonicalRows = sourceRows.map(toStaticCourseMistakeRow);
  assertUniquePublicCourseMistakes(canonicalRows);
  const ownerByStaticKey = new Map<string, string | null>();
  const unresolved = new Set<string>();

  for (const row of canonicalRows) {
    const staticKey = staticCourseMistakeLessonKey(row);
    if (!ownerByStaticKey.has(staticKey)) {
      const matches = curriculumLessonKeyAliases(staticKey).filter(
        (key) => activeLessonKeys.has(key),
      );
      if (matches.length > 1) {
        throw new Error(
          `multiple active owners for course mistake: ${matches.join(", ")}`,
        );
      }
      const owner = matches[0] ?? null;
      ownerByStaticKey.set(staticKey, owner);
      if (!owner) unresolved.add(staticKey);
    }
  }

  const rows = canonicalRows.flatMap((row) => {
    const owner = ownerByStaticKey.get(staticCourseMistakeLessonKey(row));
    if (!owner) return [];
    const course = splitCourse(owner);
    return [course === row.course ? row : ({ ...row, course } as T)];
  });
  assertUniquePublicCourseMistakes(rows);

  const courseCounts = new Map<string, number>();
  for (const row of rows) {
    courseCounts.set(row.course, (courseCounts.get(row.course) ?? 0) + 1);
  }
  const courseSlugs = courseOrder.filter((slug) => courseCounts.has(slug));
  const courseTitles = new Map(
    courseSlugs.map((slug) => [
      slug,
      materializedCourseTitle(
        slug,
        activeLessonKeys,
        catalogTitles.get(slug),
        fallbackTitles,
      ),
    ]),
  );

  const previewScore = (heading: string) =>
    (/`[^`]+`/.test(heading) ? 2 : 0) +
    (/[A-Z][A-Za-z]*(?:Error|Exception)/.test(heading) ? 2 : 0);
  const previews = new Map(
    courseSlugs.map((slug) => [
      slug,
      rows
        .filter((row) => row.course === slug)
        .slice()
        .sort(
          (left, right) =>
            previewScore(right.heading) - previewScore(left.heading) ||
            left.heading.length - right.heading.length,
        )
        .slice(0, 3)
        .map((row) => row.heading),
    ]),
  );

  return {
    rows,
    courseTitles,
    courseCounts,
    courseSlugs,
    previews,
    errorIndex: buildMaterializedErrorIndex(rows, courseTitles),
    total: rows.length,
    unresolvedLessonKeys: [...unresolved],
  };
}
